import React from 'react';
import { motion } from 'framer-motion';
import { Euro, ShoppingBasket } from 'lucide-react';
import { Product } from '../../types/Product';
import { useAuth } from '../../hooks/useAuth';
import { useShoppingList } from '../../hooks/shopping/useShoppingList';
import { ListSelector } from '../shopping/ListSelector';
import { AuthModal } from '../auth/AuthModal';

interface ProductCardProps {
  product: Product;
  onClick: () => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onClick }) => {
  const { user } = useAuth();
  const { 
    lists,
    selectedListId,
    setSelectedListId,
    addToList,
    isAdding,
    error 
  } = useShoppingList();
  const [showAuthModal, setShowAuthModal] = React.useState(false);

  const handleAddToList = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    await addToList(product);
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden group"
    >
      <div onClick={onClick} className="cursor-pointer">
        <div className="aspect-square">
          <img
            src={product.thumbnail}
            alt={product.display_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        
        <div className="p-4">
          <h3 className="font-medium text-gray-800 line-clamp-2 mb-1">
            {product.display_name}
          </h3>
          <p className="text-sm text-gray-500 mb-2">
            {product.category_name}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <Euro size={16} className="text-orange-500" />
              <span className="font-semibold text-lg">
                {product.current_price.toFixed(2)}
              </span>
            </div>
            {product.price_decreased && (
              <span className="text-sm text-gray-500 line-through">
                {product.previous_price.toFixed(2)}€
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-gray-100">
        {user && lists.length > 0 && (
          <div className="mb-3">
            <ListSelector
              lists={lists}
              selectedListId={selectedListId}
              onListSelect={setSelectedListId}
            />
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddToList}
          disabled={isAdding || (!user || !selectedListId)}
          className="w-full p-3 bg-orange-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <ShoppingBasket size={18} />
          {isAdding ? 'Añadiendo...' : user ? 'Añadir a la lista' : 'Iniciar sesión para añadir'}
        </motion.button>

        {error && (
          <p className="mt-2 text-sm text-red-500 text-center">{error}</p>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </motion.div>
  );
};